import type { Mark } from '@material-ui/core';
import { Slider } from '@material-ui/core';
import { Currency } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import { FormattedCurrencyAmount } from '@swf/poker';

import { CURRENCY_NAME_CURRENCY_MAP } from '../../../constants';
import { usePokerCashGamesWidget } from '../../../model';
import type { SliderValues } from '../../../types';
import SelectorSkeleton from '../../SelectorSkeleton/SelectorSkeleton';

import {
  TXTCLI_Buy_In,
  TXTCLI_Stakes,
  TXTWCL_OopsX_No_Games_Found,
  TXTWCL_SorryX_we_cannot_load_the_game_ELL
} from './StakeSelector.messages.json';
import styles from './StakeSelector.scss';

interface Props {
  isLoading: boolean;
  queryError?: string;
  cashGames: SliderValues[] | null;
}

const sliderClasses = {
  rail: styles.sliderRail,
  thumb: styles.sliderThumb,
  track: styles.sliderTrack,
  mark: styles.sliderMark
};

const StakeSelector: React.FC<Props> = ({ cashGames, isLoading, queryError }) => {
  const { widgetDispatch, widgetSelect } = usePokerCashGamesWidget();
  const activeSliderIndex = useSelector(widgetSelect.getActiveSliderIndex);

  if (isLoading) {
    return <SelectorSkeleton />;
  }

  if (queryError) {
    return (
      <div className={styles.error}>
        <Translation {...TXTWCL_SorryX_we_cannot_load_the_game_ELL} tagName="span" />
      </div>
    );
  }

  if (!cashGames || !cashGames.length) {
    return (
      <div className={styles.error}>
        <Translation {...TXTWCL_OopsX_No_Games_Found} tagName="span" />
      </div>
    );
  }

  const defaultSliderIndex = Math.max(0, Math.min(2, cashGames.length - 1));

  const sliderIndex =
    activeSliderIndex > cashGames.length - 1 ? defaultSliderIndex : activeSliderIndex;

  const { smallBlind, bigBlind, minBuyIn, maxBuyIn, currency } = cashGames[sliderIndex];
  const formattedCurrency = CURRENCY_NAME_CURRENCY_MAP[currency];
  const isPlayMoney = formattedCurrency === Currency.PlayMoney;

  const SLIDER_MARKS: Mark[] = cashGames.map((_: SliderValues, i: number) => ({
    value: i,
    label: ''
  }));

  return (
    <>
      <div className={styles.description}>
        <Translation {...TXTCLI_Stakes} />
      </div>
      <div className={styles.stakes}>
        <FormattedCurrencyAmount
          amount={smallBlind}
          isPlayMoney={isPlayMoney}
          currency={formattedCurrency}
          notationPM="compact"
          currencyDisplay="narrowSymbol"
        />
        {' / '}
        <FormattedCurrencyAmount
          amount={bigBlind}
          isPlayMoney={isPlayMoney}
          currency={formattedCurrency}
          notationPM="compact"
          currencyDisplay="narrowSymbol"
        />
      </div>
      <div className={styles.buyIn}>
        <Translation {...TXTCLI_Buy_In} tagName="span" />:{' '}
        <FormattedCurrencyAmount
          amount={minBuyIn}
          isPlayMoney={isPlayMoney}
          currency={formattedCurrency}
          notationPM="compact"
          currencyDisplay="narrowSymbol"
        />
        {' - '}
        <FormattedCurrencyAmount
          amount={maxBuyIn}
          isPlayMoney={isPlayMoney}
          currency={formattedCurrency}
          notationPM="compact"
          currencyDisplay="narrowSymbol"
        />
      </div>
      <Slider
        id="activeSliderIndex"
        defaultValue={defaultSliderIndex}
        value={sliderIndex}
        marks={SLIDER_MARKS}
        min={0}
        max={cashGames.length - 1}
        classes={sliderClasses}
        valueLabelDisplay="off"
        onChange={(_, val) => {
          if (typeof val === 'number') {
            widgetDispatch.setActiveSliderIndex(val);
          }
        }}
      />
    </>
  );
};

export default StakeSelector;
